/**
 * Phase 6 — Admin AI Settings page
 * Route: /admin/ai-settings
 */
import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Sparkles, ToggleLeft, ToggleRight, ShieldCheck } from 'lucide-react';
import theme from '../theme';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const cardStyle = {
  background: theme.colors.bg.card, border: `1px solid ${theme.colors.borders}`,
  borderRadius: theme.radius.card, padding: 20, boxShadow: theme.colors.shadows.sm,
};

export default function AdminAISettingsPage() {
  const auth = useMemo(() => ({ headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } }), []);
  const [enabled, setEnabled] = useState(true);
  const [configured, setConfigured] = useState(false);
  const [model, setModel] = useState('');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/admin/ai-settings/architect-assistant`, auth);
      setEnabled(!!res.data?.enabled);
      setConfigured(!!res.data?.configured);
      setModel(res.data?.model || '');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to load AI settings');
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  const toggle = async () => {
    setSaving(true);
    try {
      const res = await axios.patch(`${API}/admin/ai-settings/architect-assistant`,
        { enabled: !enabled }, auth);
      setEnabled(!!res.data?.enabled);
      toast.success(`Architect AI Assistant ${res.data?.enabled ? 'enabled' : 'disabled'}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Toggle failed');
    } finally { setSaving(false); }
  };

  return (
    <div data-testid="admin-ai-settings-page" style={{ padding: '24px 28px', maxWidth: 900, margin: '0 auto' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
        <Sparkles size={22} color={theme.colors.primary} />
        <div style={{ fontSize: 22, fontWeight: 900 }}>AI Settings</div>
      </div>
      <div style={{ color: theme.colors.text.muted, fontSize: 13, marginTop: 4, marginBottom: 14 }}>
        Enable or disable the Kickback Architect AI Assistant for the whole platform.
      </div>

      <div style={cardStyle}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6 }}>
          <ShieldCheck size={16} color={theme.colors.status.success} />
          <div style={{ fontWeight: 800, fontSize: 15 }}>Architect AI Assistant</div>
        </div>
        <div style={{ fontSize: 12, color: theme.colors.text.muted, marginBottom: 12 }}>
          Model: <code data-testid="ai-model">{model || '—'}</code> ·
          {' '}Provider configured: <b data-testid="ai-configured">{loading ? '…' : (configured ? 'yes' : 'no')}</b>
        </div>

        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <button data-testid="ai-toggle-btn" disabled={saving || loading} onClick={toggle}
            style={{ padding: '10px 14px', borderRadius: theme.radius.sm, border: 'none',
              background: enabled ? theme.colors.status.success : theme.colors.text.muted,
              color: '#fff', cursor: 'pointer', fontWeight: 700,
              display: 'inline-flex', alignItems: 'center', gap: 8 }}>
            {enabled ? <ToggleRight size={16} /> : <ToggleLeft size={16} />}
            {enabled ? 'Enabled' : 'Disabled'}
          </button>
          <div data-testid="ai-status-text" style={{ fontSize: 13, color: theme.colors.text.secondary }}>
            {loading ? 'Loading…' : (enabled
              ? 'The Architect AI Assistant is enabled. Approved Architects can generate assistant drafts on assigned requests.'
              : 'The Architect AI Assistant is disabled. New draft generation is blocked; existing drafts remain visible.')}
          </div>
        </div>

        <div style={{ marginTop: 14, padding: 10, background: '#FFF7ED', border: '1px solid #FED7AA',
          borderRadius: theme.radius.sm, fontSize: 12, color: '#7C2D12' }}>
          Drafts generated by the Assistant are for internal review only. They are not legal, tax,
          financial, investment, or compliance advice. Approved Architects must review every draft
          before it is used to shape a Kickback program.
        </div>
      </div>
    </div>
  );
}
